import { FormField, Separator, Text } from '@/_components';
import {
  CardContainer,
  FormLayout,
  FormMediaInput,
  ImageTextHeader,
  PageHeaderCtrlContainer,
  SelectorWithAvatarContainer,
  StatusButtonInput,
  useMedia,
} from '@/_containers';
import { PageHeaderContainer } from '@/_containers/headers/page/page-header-container';
import { BrandsAsyncSelect } from '@/_pages/settings/brands/shared';
import { PartnersSelect } from '@/_pages/settings/partners/shared';
import { RelationshipScoresSelect } from '@/_pages/settings/relationship-scores/shared';
import { TeamMembersAsyncSelect } from '@/_pages/team-members/shared/components/team-members-async-select';
import type { OnPartialUpdate } from '@/_schemas/types';
import { nameResolver } from '@/_shared';

import { useCompanyFormContext } from '../../form-context';
import type { CompanyUpdateSchema } from '../../schemas';

export function Header({
  onPartialUpdate,
  isPending,
  isProfilePage,
  isEditable,
}: {
  isProfilePage?: boolean;
  isPending: boolean;
  isEditable: boolean;
  onPartialUpdate: OnPartialUpdate<CompanyUpdateSchema>;
}) {
  const form = useCompanyFormContext();
  const [name, short_name, id, account_number, brand, partner, relationship_score, account_manager] = form.watch([
    'name',
    'short_name',
    'id',
    'account_number',
    'brand',
    'partner',
    'relationship_score',
    'account_manager',
  ]);
  const folder_path = `client/${id}`;

  const { onImageUpdate } = useMedia(onPartialUpdate);

  return (
    <CardContainer className="pb-2">
      <section className="flex flex-1 items-center justify-center gap-4">
        <aside className="absolute top-7 left-7">
          <div className="text-c-card-muted text-xs font-semibold">Acc. No. {account_number || '—'}</div>
        </aside>

        <div className="mt-8">
          <PageHeaderContainer>
            <ImageTextHeader
              imageSlot={
                <div className="h-24.5">
                  <FormField
                    control={form.control}
                    name="logo"
                    render={({ field: { ref: _, ...field } }) => (
                      <FormMediaInput
                        {...field}
                        shape="rectangle"
                        addPadding
                        height={192}
                        width={244}
                        label=""
                        disabled={!isEditable}
                        folder_path={folder_path}
                        onImageStateChange={onImageUpdate}
                      />
                    )}
                  />
                </div>
              }
              primarySlot={nameResolver(name, undefined)}
              secondarySlot={
                <Text as="small" className="text-c-card-muted mt-1 font-normal">
                  {short_name || ''}
                </Text>
              }
              tertiarySlot={
                <PageHeaderCtrlContainer>
                  <StatusButtonInput isPending={isPending} onUpdate={onPartialUpdate} />
                </PageHeaderCtrlContainer>
              }
            />
          </PageHeaderContainer>
        </div>
      </section>

      {isProfilePage && (
        <>
          <Separator className="bg-outline/40 mt-9 dark:bg-gray-600/40" />
          <FormLayout>
            <SelectorWithAvatarContainer
              disabled={!isEditable}
              src={brand?.icon?.at(0)?.url || ''}
              label="Brand"
              name={brand?.name ?? ''}
              className="lg:items-center"
              avatarContainerClassName="lg:border-none mb-0"
            >
              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <BrandsAsyncSelect
                    label="Brand"
                    value={field.value}
                    disabled={!isEditable}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </SelectorWithAvatarContainer>

            <SelectorWithAvatarContainer
              disabled={!isEditable}
              src={partner?.icon?.at(0)?.url || ''}
              label="Partner"
              name={partner?.name ?? ''}
              className="lg:items-center"
              avatarContainerClassName="lg:border-none mb-0"
            >
              <FormField
                control={form.control}
                name="partner"
                render={({ field }) => (
                  <PartnersSelect
                    label="Partner"
                    value={field.value ?? null}
                    disabled={!isEditable}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </SelectorWithAvatarContainer>

            <SelectorWithAvatarContainer
              disabled={!isEditable}
              src={relationship_score?.icon?.at(0)?.url || ''}
              label="Health Score"
              name={relationship_score?.label ?? ''}
              className="lg:items-center"
              avatarContainerClassName="lg:border-none mb-0"
            >
              <FormField
                control={form.control}
                name="relationship_score"
                render={({ field }) => (
                  <RelationshipScoresSelect
                    label="Health Score"
                    value={field.value}
                    disabled={!isEditable}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </SelectorWithAvatarContainer>

            <SelectorWithAvatarContainer
              disabled={!isEditable}
              src={account_manager?.profile_image?.at(0)?.url || ''}
              label="Account Manager"
              name={nameResolver(account_manager?.first_name, account_manager?.last_name, '')}
              className="lg:items-center"
              avatarContainerClassName="lg:border-none mb-0"
            >
              <FormField
                control={form.control}
                name="account_manager"
                render={({ field }) => (
                  <TeamMembersAsyncSelect
                    label="Account Manager"
                    value={field.value ?? null}
                    disabled={!isEditable}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </SelectorWithAvatarContainer>
          </FormLayout>
        </>
      )}
    </CardContainer>
  );
}
